import { useEffect, useState } from 'react';
import { getWalletBalance } from '@/services/wallet';

type TBalanceResult = { walletId: string; balance: number | null };

/**
 * Fetches a wallet's live balance, deliberately uncached - the balance shown in HomeContext's
 * wallet list can be stale by the time a payment modal is opened, and callers use this to judge
 * whether the account can cover a payment right now. Fails safe to `null` so callers can fall back
 * to their generic copy when the balance is unknown.
 *
 * isLoading is derived rather than set in the effect, so it's already true on the very first
 * enabled render - callers can hold a placeholder instead of flashing their fallback copy first.
 */
export const useWalletBalance = (walletId?: string, enabled = true) => {
  const [result, setResult] = useState<TBalanceResult | null>(null);

  useEffect(() => {
    // Dropped on disable so reopening never shows a previous session's balance, even briefly.
    if (!walletId || !enabled) {
      setResult(null);
      return;
    }

    let isCancelled = false;

    getWalletBalance(walletId)
      .then((response) => {
        if (!isCancelled) setResult({ walletId, balance: response.data.balance ?? null });
      })
      .catch(() => {
        if (!isCancelled) setResult({ walletId, balance: null });
      });

    return () => {
      isCancelled = true;
    };
  }, [walletId, enabled]);

  const isActive = enabled && !!walletId;
  const isSettled = isActive && result?.walletId === walletId;

  return {
    balance: isSettled ? result.balance : null,
    isLoading: isActive && !isSettled
  };
};
